import { useEffect, useState } from 'react';
import { fetchCohortTrend, fetchTargets, fetchGuardrail, fetchNewVsReturning } from '../lib/api';
import { ImpactBar } from './Charts';

export default function ImpactTab({ filters }) {
  const [trend, setTrend] = useState(null);
  const [targets, setTargets] = useState(null);
  const [guardrail, setGuardrail] = useState(null);
  const [newret, setNewret] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    (async () => {
      setBusy(true);
      const [t, tg, g, nr] = await Promise.all([
        fetchCohortTrend(filters.category),
        fetchTargets(filters.category),
        fetchGuardrail(filters.category),
        fetchNewVsReturning(filters.category),
      ]);
      setTrend(t.data); setTargets(tg.data); setGuardrail(g.data); setNewret(nr.data);
      setBusy(false);
    })();
  }, [filters.category]);

  if (busy || !trend || !targets) return <div className="loading">Loading impact…</div>;

  const catLabel = filters.category === 'ALL' ? 'All (Food+Treats)' : (filters.category === 'FOOD' ? 'Food' : 'Treats');
  const q = targets.m1?.quarterly?.join(' → ') || '';
  const q3 = targets.m3?.quarterly?.join(' → ') || '';

  return (
    <>
      <div className="explainer">
        <b>Program impact scoreboard · {catLabel}</b> · Workstream A raises M1 conversion from
        {' '}<b>{targets.m1.baseline}% to {targets.m1.final}%</b> over 12 months (milestones {q}%).
        Workstream B raises M3 retention from <b>{targets.m3.baseline}% to {targets.m3.final}%</b> ({q3}%).
        Baselines re-compute weekly from the last 4 mature cohorts. Apr-2025 excluded (file-start effect).
      </div>

      <div className="panel panel-lead">
        <div className="panel-head">
          <div>
            <div className="panel-title">Workstream A · M0→M1 conversion by cohort</div>
            <div className="panel-sub">Baseline {targets.m1.baseline}% (rolling median, last 4 mature). Q4 target {targets.m1.final}%.</div>
          </div>
        </div>
        <ImpactBar trend={trend} metricKey="m1_pct" target={targets.m1.final} baseline={targets.m1.baseline} />
      </div>

      <div className="panel panel-lead">
        <div className="panel-head">
          <div>
            <div className="panel-title">Workstream B · M3 retention by cohort</div>
            <div className="panel-sub">Baseline {targets.m3.baseline}%. Q4 target {targets.m3.final}%. Every 1pp of M3 lift compounds through M6, M9, M12.</div>
          </div>
        </div>
        <ImpactBar trend={trend} metricKey="m3_pct" target={targets.m3.final} baseline={targets.m3.baseline} />
      </div>

      {guardrail && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Guardrail · discount % of MRP by month</div>
              <div className="panel-sub">Cap at {targets.disc?.final ?? 12}%. Deep-discount months correlate with weaker retention: protect retention AND margin.</div>
            </div>
          </div>
          <SimpleLine data={guardrail} valKey="disc_pct" xKey="month" cap={targets.disc?.final ?? 12} />
        </div>
      )}

      {newret && (
        <div className="panel">
          <div className="panel-head">
            <div>
              <div className="panel-title">Base quality · new vs returning customer mix</div>
              <div className="panel-sub">Returning share of monthly actives, the "we're building a repeat base" leading indicator.</div>
            </div>
          </div>
          <SimpleLine data={newret} valKey="new_pct" xKey="month" />
        </div>
      )}
    </>
  );
}

// Minimal reusable line chart for guardrail + mix
function SimpleLine({ data, valKey, xKey, cap }) {
  const W = 900, H = 240, padL = 44, padR = 24, padT = 20, padB = 40;
  const plotW = W-padL-padR, plotH = H-padT-padB;
  if (!data.length) return null;
  const values = data.map(d => d[valKey]).filter(v => v != null);
  const vMax = Math.max(...values, cap || 0) * 1.15;
  const yFor = v => padT + (1 - v/vMax) * plotH;
  const n = data.length;
  const xFor = i => padL + (i/(n-1)) * plotW;
  const linePath = data.map((d,i) => `${i===0?'M':'L'}${xFor(i)} ${yFor(d[valKey])}`).join(' ');
  const gridSteps = [0, vMax/2, vMax];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%">
      {gridSteps.map(v => (
        <g key={v}>
          <line x1={padL} y1={yFor(v)} x2={W-padR} y2={yFor(v)} stroke="#EAD9C0" strokeWidth="0.5" />
          <text x={padL-6} y={yFor(v)+3} textAnchor="end" fontFamily="IBM Plex Mono" fontSize="10" fill="#8A7B67">{v.toFixed(0)}%</text>
        </g>
      ))}
      {cap != null && (
        <g>
          <line x1={padL} y1={yFor(cap)} x2={W-padR} y2={yFor(cap)} stroke="#C0392B" strokeWidth="1.5" strokeDasharray="6 3" />
          <text x={W-padR-4} y={yFor(cap)-4} textAnchor="end" fontFamily="IBM Plex Mono" fontSize="10" fill="#C0392B" fontWeight="700">Cap {cap}%</text>
        </g>
      )}
      <path d={linePath} stroke="#E9591C" strokeWidth="2" fill="none" />
      {data.map((d,i) => (
        <circle key={i} cx={xFor(i)} cy={yFor(d[valKey])} r="3" fill="#E9591C" />
      ))}
      {data.map((d,i) => (
        <text key={`x${i}`} x={xFor(i)} y={H-20} textAnchor="middle" fontFamily="IBM Plex Mono" fontSize="10" fill="#8A7B67">{d[xKey].slice(5)}</text>
      ))}
    </svg>
  );
}
